import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { RefreshCw, Activity, Moon, CheckCircle2, AlertCircle } from "lucide-react";
import { Card, SectionLabel } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { relativeDistance } from "@/lib/dates";
import {
  getSyncStatus,
  triggerIntervalsSync,
  triggerWellnessSync,
} from "@/api/sync";
import { useState } from "react";

export function SyncStatusCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["sync-status"],
    queryFn: getSyncStatus,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      await Promise.all([triggerIntervalsSync(), triggerWellnessSync()]);
      await qc.invalidateQueries({ queryKey: ["sync-status"] });
      await qc.invalidateQueries({ queryKey: ["dashboard"] });
    } finally {
      setBusy(false);
    }
  };

  if (q.isLoading || !q.data) {
    return (
      <Card>
        <SectionLabel>{t("sync_status.section_label")}</SectionLabel>
        <p className="mt-3 text-[13px] text-taupe">{t("sync_status.loading")}</p>
      </Card>
    );
  }
  const d = q.data;

  return (
    <Card>
      <div className="flex items-center justify-between">
        <SectionLabel>{t("sync_status.section_label")}</SectionLabel>
        {d.connected ? (
          <span className="inline-flex items-center gap-1 text-[11px] text-green-700">
            <CheckCircle2 className="h-3 w-3" strokeWidth={2} />
            {t("sync_status.connected")}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11px] text-coral-dark">
            <AlertCircle className="h-3 w-3" strokeWidth={2} />
            {t("sync_status.disconnected")}
          </span>
        )}
      </div>

      {!d.connected ? (
        <p className="mt-3 text-[13px] text-taupe">
          {t("sync_status.connect_hint")}
        </p>
      ) : (
        <>
          <div className="mt-3 space-y-2 text-[12px]">
            <Row
              icon={<RefreshCw className="h-3.5 w-3.5" strokeWidth={1.8} />}
              label={t("sync_status.next_run")}
              value={
                d.hourly_job.next_run_at
                  ? relativeDistance(d.hourly_job.next_run_at)
                  : t("sync_status.disabled")
              }
            />
            <Row
              icon={<Activity className="h-3.5 w-3.5" strokeWidth={1.8} />}
              label={t("sync_status.last_workout")}
              value={
                d.last_workout
                  ? `${d.last_workout.date}${d.last_workout.title_hint ? ` — ${d.last_workout.title_hint}` : ""}`
                  : t("sync_status.no_data")
              }
            />
            <Row
              icon={<Moon className="h-3.5 w-3.5" strokeWidth={1.8} />}
              label={t("sync_status.last_wellness")}
              value={
                d.last_wellness && d.last_wellness.synced_at
                  ? t("sync_status.wellness_pull_inline", {
                      date: d.last_wellness.date,
                      distance: relativeDistance(d.last_wellness.synced_at),
                    })
                  : t("sync_status.no_data")
              }
            />
          </div>
          <div className="mt-4">
            <Button
              onClick={run}
              disabled={busy}
              variant="secondary"
              className="inline-flex items-center gap-1.5"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`}
                strokeWidth={2}
              />
              {busy ? t("sync_status.syncing") : t("sync_status.sync_now")}
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}

function Row({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-2">
      <span className="mt-0.5 text-taupe">{icon}</span>
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-wider text-taupe">
          {label}
        </div>
        <div className="text-navy-deep">{value}</div>
      </div>
    </div>
  );
}
